// Issue #535: world picker route.
//
// Lists every world exposed by GET /api/worlds and lets the player
// drop into one. Clicking "Play" POSTs /api/sessions {world_name} —
// the backend get-or-creates the per-(user, world) session — then
// stores the chosen slug in localStorage so reloads land back in the
// same world instead of bouncing through the picker again.
//
// Before this route existed, /play silently routed every account to
// the engine's boot world (the fallback added in #532/#533), which
// masked the multi-world wiring entirely. Now /play with no remembered
// world redirects here; the boot-world fallback is retained inside
// GameContext as a transitional safety net for sessions that were
// created before this route shipped.
//
// The picker deliberately does *not* mount GameProvider — that
// provider's boot effect would auto-create a session against
// /api/session (the legacy boot-world endpoint) the moment the page
// loaded, which is exactly the silent-routing bug this issue exists
// to undo. Instead we render a stripped-down picker-only header.
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  ApiError,
  api,
  LAST_WORLD_STORAGE_KEY,
  type SessionByWorldResponse,
  type WorldListResponse,
  type WorldSummary,
} from "../game/api";
import { hasClerk } from "../auth/clerk";
import SignOutButton from "../components/SignOutButton";

const TILE_BASE =
  "block w-full text-left rounded-lg border border-lucid-accent/40 bg-lucid-elevated/40 " +
  "p-5 transition-colors hover:border-lucid-accent hover:bg-lucid-elevated focus:outline-none " +
  "focus:ring-2 focus:ring-lucid-accent";

const PLAY_BUTTON =
  "inline-block mt-4 px-4 py-2 rounded-md border border-lucid-accent/60 text-lucid-accent " +
  "font-ui text-sm hover:bg-lucid-elevated hover:border-lucid-accent transition-colors " +
  "disabled:opacity-50 disabled:cursor-not-allowed";

function truncate(text: string | null | undefined, max = 240): string {
  if (!text) return "";
  if (text.length <= max) return text;
  return text.slice(0, max).trimEnd() + "…";
}

export default function WorldPicker() {
  const navigate = useNavigate();
  const [worlds, setWorlds] = useState<WorldSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api<WorldListResponse>("/api/worlds");
        if (cancelled) return;
        // Tutorial first so new players land on the introduction;
        // everything else stays in API order (alpha, per #635).
        const sorted = [...(data.worlds ?? [])].sort((a, b) => {
          if (a.world_name === "tutorial") return -1;
          if (b.world_name === "tutorial") return 1;
          return 0;
        });
        setWorlds(sorted);
      } catch (e) {
        if (cancelled) return;
        const err = e as ApiError;
        setError(err.message || "Failed to load worlds.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const pickWorld = useCallback(
    async (worldName: string) => {
      if (pending) return;
      setPending(worldName);
      setError(null);
      try {
        await api<SessionByWorldResponse>("/api/sessions", "POST", {
          world_name: worldName,
        });
        try {
          localStorage.setItem(LAST_WORLD_STORAGE_KEY, worldName);
        } catch {
          /* private-mode etc — non-fatal, the URL param still carries us */
        }
        navigate(`/play?world=${encodeURIComponent(worldName)}`);
      } catch (e) {
        const err = e as ApiError;
        setError(`Failed to start world ${worldName}: ${err.message}`);
        setPending(null);
      }
    },
    [navigate, pending],
  );

  return (
    <main className="min-h-screen w-full bg-lucid-deep text-lucid-text">
      <div id="app">
        <header id="header">
          <div id="header-left">
            <Link
              id="home-link"
              to="/"
              title="Back to home"
              aria-label="Back to home"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12 12 3l9 9" />
                <path d="M5 10v10h14V10" />
              </svg>
            </Link>
            <span id="game-title">Worlds</span>
          </div>
          <div id="header-right">{hasClerk() && <SignOutButton />}</div>
        </header>

      <section className="px-6 py-8">
        <div className="max-w-3xl mx-auto space-y-6">
          <header className="space-y-2">
            <h1 className="font-narration text-3xl text-lucid-accent">
              Choose a World
            </h1>
            <p className="font-narration text-lucid-text/80">
              Each world is its own running game. Your progress in one
              doesn't bleed into the others — pick one to enter, and use
              the Worlds button up top to switch back anytime.
            </p>
          </header>

          {error && (
            <div className="rounded-md border border-red-500/50 bg-red-900/20 p-3 text-red-200 text-sm">
              {error}
            </div>
          )}

          {worlds === null && !error && (
            <p className="text-lucid-text/60">Loading worlds…</p>
          )}

          {worlds !== null && worlds.length === 0 && (
            <p className="text-lucid-text/60">
              No worlds are published. Ask an author to run{" "}
              <code>lucid-author publish</code>.
            </p>
          )}

          {worlds && worlds.length > 0 && (
            <ul className="grid gap-4 sm:grid-cols-1">
              {worlds.map((w) => (
                <li key={w.world_name}>
                  <article className={TILE_BASE}>
                    <div className="flex items-baseline justify-between gap-3">
                      <h2 className="font-narration text-xl text-lucid-accent">
                        {w.title || w.world_name}
                      </h2>
                      <div className="flex items-center gap-2">
                        {w.coming_soon && (
                          <span className="font-ui text-[10px] uppercase tracking-wider text-lucid-accent/80 border border-lucid-accent/40 rounded px-2 py-0.5">
                            Coming soon
                          </span>
                        )}
                        <span className="font-ui text-xs text-lucid-text/50">
                          {w.world_name}
                        </span>
                      </div>
                    </div>
                    {w.blurb && (
                      <p className="mt-2 font-narration text-lucid-text/80 leading-relaxed">
                        {truncate(w.blurb)}
                      </p>
                    )}
                    <button
                      type="button"
                      className={PLAY_BUTTON}
                      onClick={() => void pickWorld(w.world_name)}
                      disabled={pending !== null || !!w.coming_soon}
                      title={w.coming_soon ? "Coming soon" : undefined}
                    >
                      {w.coming_soon
                        ? "Coming soon"
                        : pending === w.world_name
                          ? "Entering…"
                          : "Play"}
                    </button>
                  </article>
                </li>
              ))}
            </ul>
          )}

          <div className="pt-2">
            <Link to="/" className="font-ui text-sm text-lucid-text/60 hover:text-lucid-accent">
              ← Back to home
            </Link>
          </div>
        </div>
      </section>
      </div>
    </main>
  );
}
